import type {ReactNode} from 'react'
import {exhaustive} from '@freckle/exhaustive'
import type {ButtonStyleT} from '@freckle/educator-materials/ts/components/button'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {Modal, Header, Body, Footer} from '@freckle/educator-materials/ts/components/modal'
import * as style from './confirmation-modal.module.scss'

type BsSize = 'lg' | 'sm'

type Props = {
  title: string
  showModal: boolean
  onClickYes: () => void
  onClose: () => void
  onHide?: (() => void) | null
  yesText?: string
  yesBtnStyle?: ButtonStyleT
  yesBtnDataTest?: string
  noText?: string
  noBtnDataTest?: string
  bsSize?: BsSize
  children?: ReactNode
  className?: string | null
}

export function ConfirmationModal({
  title,
  showModal,
  onClickYes,
  onClose,
  onHide,
  yesText = 'Yes',
  yesBtnStyle = 'primary',
  yesBtnDataTest,
  noText = 'No',
  noBtnDataTest,
  bsSize,
  children,
  className,
}: Props) {
  const dialogClassName =
    className !== null && className !== undefined ? {dialogClassName: className} : {}

  const onHideOption = onHide ?? onClose

  return (
    <Modal show={showModal} {...dimensions(bsSize)} onHide={onHideOption} {...dialogClassName}>
      <Header onClickClose={onHideOption}>{title}</Header>

      <Body>{children}</Body>
      <Footer>
        <div className={style.footer}>
          <Button style="tertiary" onClick={onClose} dataTest={noBtnDataTest}>
            {noText}
          </Button>
          <Button style={yesBtnStyle} onClick={onClickYes} dataTest={yesBtnDataTest}>
            {yesText}
          </Button>
        </div>
      </Footer>
    </Modal>
  )
}

const dimensions = (
  size: BsSize | undefined,
): Pick<Parameters<typeof Modal>[0], 'height' | 'width'> => {
  switch (size) {
    case 'lg':
      return {height: 'large', width: 'medium'}
    case 'sm':
      return {height: 'small', width: 'small'}
    case undefined:
      return {height: 'medium', width: 'small'}
    default:
      return exhaustive(size)
  }
}
